/**
 * Pure query-shape helpers for the Algolia-backed winder CATALOGUE.
 *
 * Sibling to `manufacturerCatalogueQuery.ts`, and modelled on it deliberately:
 * the winder catalog is a small collection every consumer wants in full, so
 * there is one whole-index fetch and no picker/listing split. The Curated
 * Winders listing's search box, deprecated toggle and column sorts all run
 * client-side over that in-memory array, exactly as they did when the tab read
 * Firestore — a server-side facet would be a round trip to narrow a list already
 * in the browser.
 *
 * Kept out of `algolia.ts` so the parameter building and the hit → entry mapping
 * can be unit-tested without pulling in the Algolia client, and the
 * `import.meta.env` credential read at its module scope.
 *
 * ⚠️ THIS PROJECTION IS FOR LISTING, NOT FOR EDITING. An entry is what the table
 * renders and what the bulk field patches read; it is NOT a document to write
 * back. `CuratedWinders.openEdit` re-reads the document from Firestore before
 * the editor opens, exactly as `openEditor` does in CuratedCalibres.
 *
 * That rule was learned the expensive way. This module used to say the
 * projection was COMPLETE — every `WinderEntry` field indexed, so a row could go
 * straight into the editor — and PR #528 doubled down by adding
 * `imageStoragePath`, `startDelayMaxHours` and `safeSpecs` here to close a gap.
 * The premise was wrong twice over: the deployed indexer never carried
 * `imageStoragePath` (30 documents held one; 0 index records did), and
 * `saveWinder` nulls every optional field absent from its input, so the editor
 * opened blank and the next save DELETED the path from the document. All three
 * came off the list again — with hydration they were inert, and leaving them
 * implied a contract the index does not honour.
 *
 * `startDelayMaxHours` has since come BACK, and the distinction is the whole
 * rule: the listing now reads it, to classify a pre-`applianceKind` entry by
 * whether it winds. It earns its place by being displayed-through, not by
 * completing a set. `imageStoragePath` and `safeSpecs` stay out.
 *
 * So `WinderCatalogueEntry` stays a whole `WinderEntry` for convenience of
 * typing, but treat it as a row: a field may be absent because the indexer never
 * shipped it, not because the document lacks it. Add an attribute below when the
 * LISTING displays it, and for no other reason.
 */

import { Timestamp } from 'firebase/firestore';
import type { StorageApplianceKind, WinderEntry } from '../types';
import { STORAGE_APPLIANCE_KINDS } from './winderApplianceKind';
import { normalizeTurnsPerDayRange } from './winderTurnsPerDay';

/**
 * Attributes the catalogue retrieves — the whole indexed surface.
 *
 * Requesting an attribute the index does not hold is silently dropped rather
 * than an error, so this list is only as good as the indexer it mirrors
 * (`buildWinderIndexRecord`). Verified against that builder field by field.
 */
export const WINDER_CATALOGUE_ATTRIBUTES = [
  'objectID',
  /*
   * Winder, safe or display case. Retrieved because the listing SEGMENTS on it
   * — see the rule in the module header, which this satisfies rather than
   * bends.
   *
   * Its watchlock counterpart landed as #1022, whose commit body carries the
   * matching ⚠️ in the other direction. The two lists are one contract read from
   * two ends.
   */
  'applianceKind',
  'make',
  'makeSlug',
  'model',
  'displayName',
  'countryOfManufacture',
  'slotCount',
  'turnsPerDay',
  'turnsPerDayRange',
  'rotationDirection',
  'rotationProgramSlugs',
  'powerSourceSlugs',
  'materialSlugs',
  'isQuiet',
  'hasLock',
  'hasDisplayCase',
  'isSafe',
  /*
   * Retrieved for the SEGMENT, not for a column.
   *
   * `resolveApplianceKind` classifies the entries that predate `applianceKind`
   * by whether they wind, and this is one of the five fields it reads. Without
   * it a legacy Buben & Zörweg whose only winding evidence is a start delay
   * would file under Safes here while filing under Winders in the RN picker —
   * the two would disagree about the same document.
   *
   * It was removed from this list once before, along with `imageStoragePath` and
   * `safeSpecs`, because the listing then fed rows straight to the editor and a
   * partial projection DELETED fields on the next save. `openEdit` hydrates from
   * Firestore now, so a retrieved attribute is inert for writes; this one comes
   * back because the listing genuinely reads it. The other two stay out — no
   * listing surface needs them, and the header's rule is "add an attribute when
   * the LISTING displays it, and for no other reason".
   */
  'startDelayMaxHours',
  'priceTierSlug',
  'description',
  'aliases',
  'sourceUrls',
  'aiConfidence',
  'createdBy',
  'isDeprecated',
  'createdAt',
  'updatedAt',
] as const;

/** The `rotationDirection` values the app understands, as a runtime set. */
const KNOWN_ROTATION_DIRECTIONS = new Set<string>([
  'clockwise',
  'counter_clockwise',
  'bidirectional',
]);

/** The `aiConfidence` values the app understands, as a runtime set. */
const KNOWN_AI_CONFIDENCE = new Set<string>(['high', 'medium', 'low']);

/**
 * The `applianceKind` values the app understands, as a runtime set.
 *
 * Validated on the way OUT of the index as well as on the way in
 * (`buildWinderIndexRecord` does the same check), because the value decides
 * which half of the editor renders and which segment a row files under. An
 * unexpected string is dropped, which lands the row back on the
 * absent-means-winder default rather than in a state nothing handles.
 *
 * ⚠️ DERIVED FROM THE UNION, NEVER LISTED HERE. This set used to be the literal
 * `['winder', 'safe']`, written before display cases existed and never grown
 * when they arrived. Dropping an unknown value is a safe default for a typo; it
 * is a data-loss bug for a kind the app genuinely supports, and it failed
 * silently in exactly the way this validator was meant to prevent — every
 * display case arrived at the listing with no `applianceKind` at all, resolved
 * to "winder" on the absent-means-winder fallback, and put the entire catalogue
 * in the Winders segment.
 *
 * The indexer at the other end (`buildWinderIndexRecord`, watchlock) had all
 * three the whole time. Only the reader was short.
 */
const KNOWN_APPLIANCE_KINDS = new Set<string>(STORAGE_APPLIANCE_KINDS);

/** One `watchkeeper_winders` record, as the catalogue consumes it. */
export interface WinderCatalogueHit {
  /** The winder's Firestore doc id — `winders/{objectID}`. */
  objectID: string;
  make?: string;
  makeSlug?: string;
  model?: string;
  displayName?: string;
  countryOfManufacture?: string;
  slotCount?: number;
  turnsPerDay?: number[];
  /** The alternative TPD shape — a nested object on the record. */
  turnsPerDayRange?: { min?: number; max?: number };
  /** A plain string on the record — see {@link KNOWN_ROTATION_DIRECTIONS}. */
  rotationDirection?: string;
  rotationProgramSlugs?: string[];
  powerSourceSlugs?: string[];
  materialSlugs?: string[];
  isQuiet?: boolean;
  hasLock?: boolean;
  hasDisplayCase?: boolean;
  isSafe?: boolean;
  /** A plain string on the record — see {@link KNOWN_APPLIANCE_KINDS}. */
  applianceKind?: string;
  startDelayMaxHours?: number;
  priceTierSlug?: string;
  description?: string;
  aliases?: string[];
  sourceUrls?: string[];
  /** A plain string on the record — see {@link KNOWN_AI_CONFIDENCE}. */
  aiConfidence?: string;
  createdBy?: string;
  isDeprecated?: boolean;
  /** Epoch milliseconds — the indexer flattens Firestore Timestamps. */
  createdAt?: number;
  /** Epoch milliseconds. */
  updatedAt?: number;
}

/**
 * A winder as the catalogue serves it.
 *
 * The full `WinderEntry`, because the index carries the full document — see the
 * module header for why anything narrower would be a data-loss bug rather than a
 * missing column.
 */
export type WinderCatalogueEntry = WinderEntry;

/**
 * Algolia search parameters for one page of the catalogue.
 *
 * No `facets` and no `facetFilters`: the listing holds the whole catalogue
 * locally and filters client-side.
 *
 * `attributesToHighlight: []` because no winder surface renders highlight
 * markup. `analytics: false` keeps admin browsing out of the app's search
 * analytics. `cacheable: false` bypasses the SDK's in-memory response cache, so
 * the Refresh button after an edit shows the edit rather than replaying the
 * pre-edit response.
 */
export function buildWinderCatalogueParams(
  page: number,
  hitsPerPage: number,
): Record<string, unknown> {
  return {
    hitsPerPage,
    page,
    analytics: false,
    cacheable: false,
    attributesToRetrieve: [...WINDER_CATALOGUE_ATTRIBUTES],
    attributesToHighlight: [],
  };
}

/**
 * Map one Algolia hit onto the shape the admin's winder surfaces render.
 *
 * Empty strings and empty arrays are DROPPED rather than carried across. The
 * indexer writes its string and array attributes unconditionally (`asString(...)`
 * yields `''` for a missing field, `asStringArray(...)` yields `[]`), so a
 * straight copy would turn every absent `displayName` / `countryOfManufacture`
 * into a present-but-empty one — and `displayName || model`, the label idiom the
 * winder surfaces use, would then render nothing where it used to fall back to
 * the model.
 *
 * It matters more than cosmetics here: an entry is fed back into `saveWinder`,
 * which writes what it is given. Dropping the empties keeps a hit
 * indistinguishable from the Firestore document behind it, so the Algolia path
 * and the Firestore fallback save identically.
 *
 * The three boolean spec flags and `slotCount` are carried across verbatim
 * INCLUDING `false` / `0`: the indexer omits them when absent, so their presence
 * is already the signal that the admin answered the question. `isDeprecated`
 * defaults to `false` because the indexer always writes it.
 *
 * Reconstructing `Timestamp`s from the indexed epoch milliseconds keeps
 * consumers that call `.toMillis()` — the listing's date column and its
 * updated-at sort — working on a hit-derived entry exactly as they did on a
 * Firestore document.
 */
export function winderHitToEntry(hit: WinderCatalogueHit): WinderCatalogueEntry {
  const entry = {
    id: hit.objectID,
    make: hit.make || '',
    makeSlug: hit.makeSlug || '',
    model: hit.model || '',
    isDeprecated: hit.isDeprecated === true,
  } as WinderCatalogueEntry;

  if (hit.displayName) entry.displayName = hit.displayName;
  if (hit.countryOfManufacture) entry.countryOfManufacture = hit.countryOfManufacture;
  if (typeof hit.slotCount === 'number') entry.slotCount = hit.slotCount;
  if (hit.turnsPerDay?.length) entry.turnsPerDay = hit.turnsPerDay;
  // Only a usable range is carried across — a half-indexed object would be a
  // range the editor opens in range mode and cannot show, and the next save
  // would clear the list it displaced.
  const range = normalizeTurnsPerDayRange(hit.turnsPerDayRange);
  if (range) entry.turnsPerDayRange = range;
  if (hit.rotationDirection && KNOWN_ROTATION_DIRECTIONS.has(hit.rotationDirection)) {
    entry.rotationDirection =
      hit.rotationDirection as WinderCatalogueEntry['rotationDirection'];
  }
  if (hit.rotationProgramSlugs?.length) {
    entry.rotationProgramSlugs = hit.rotationProgramSlugs;
  }
  if (hit.powerSourceSlugs?.length) entry.powerSourceSlugs = hit.powerSourceSlugs;
  if (hit.materialSlugs?.length) entry.materialSlugs = hit.materialSlugs;
  if (typeof hit.isQuiet === 'boolean') entry.isQuiet = hit.isQuiet;
  if (typeof hit.hasLock === 'boolean') entry.hasLock = hit.hasLock;
  if (typeof hit.hasDisplayCase === 'boolean') {
    entry.hasDisplayCase = hit.hasDisplayCase;
  }
  if (typeof hit.isSafe === 'boolean') entry.isSafe = hit.isSafe;
  // Absent stays absent, matching the indexer: a record with no `applianceKind`
  // describes a document with none, and `resolveApplianceKind` is what turns
  // that into an answer.
  if (hit.applianceKind && KNOWN_APPLIANCE_KINDS.has(hit.applianceKind)) {
    entry.applianceKind = hit.applianceKind as StorageApplianceKind;
  }
  if (typeof hit.startDelayMaxHours === 'number') {
    entry.startDelayMaxHours = hit.startDelayMaxHours;
  }
  if (hit.priceTierSlug) entry.priceTierSlug = hit.priceTierSlug;
  if (hit.description) entry.description = hit.description;
  if (hit.aliases?.length) entry.aliases = hit.aliases;
  if (hit.sourceUrls?.length) entry.sourceUrls = hit.sourceUrls;
  if (hit.aiConfidence && KNOWN_AI_CONFIDENCE.has(hit.aiConfidence)) {
    entry.aiConfidence = hit.aiConfidence as WinderCatalogueEntry['aiConfidence'];
  }
  if (hit.createdBy) entry.createdBy = hit.createdBy;

  // `WinderEntry` declares these as `Date` (the shared-types cross-platform
  // default), but the admin reads Firestore directly and every other winder path
  // hands the UI a `Timestamp`. Matching that here — rather than a real `Date` —
  // is what keeps `tsToMillis` in the listing working on both paths.
  if (typeof hit.createdAt === 'number') {
    entry.createdAt = Timestamp.fromMillis(hit.createdAt) as unknown as Date;
  }
  if (typeof hit.updatedAt === 'number') {
    entry.updatedAt = Timestamp.fromMillis(hit.updatedAt) as unknown as Date;
  }
  return entry;
}

/**
 * Sort a catalogue by `model`, ascending.
 *
 * Exists so the Algolia path reproduces `getWinders()`'s `orderBy('model')`
 * exactly. The listing re-sorts by its own column choice on top of this, but its
 * initial state IS model-ascending, and `getWindersForMake` consumers render the
 * order they are given.
 *
 * `localeCompare` rather than `<`, so accented model names file where a reader
 * expects rather than after "Z" — a small deliberate improvement on Firestore's
 * bytewise ordering, matching `sortManufacturersByName`.
 */
export function sortWindersByModel<T extends { model: string }>(entries: T[]): T[] {
  return [...entries].sort((a, b) => (a.model || '').localeCompare(b.model || ''));
}
